import TileLayer from 'ol/layer/Tile'
import TileWMS from 'ol/source/TileWMS'
import {
  pointInfoUpdate,
  showLayerLegend,
  showLayerLegendA,
  showLayerLegendB,
} from 'actions/map'
import useMap from 'hooks/use-map'
import { get, isNil } from 'es-toolkit/compat'
import {
  fetchAreaInfo,
  fetchPointInfo,
  fetchSwathInfo,
  fetchElevationProfile,
} from 'api/field'
import { requestYieldUnit, isLegacySLD } from 'common/misc'
import { unitConvert } from '@farmtrx/units'
import { useSelector, useDispatch } from 'react-redux'
import { useEffect, useState, useRef } from 'react'
import { transform } from 'ol/proj'
import {
  geoserverLoadFunction,
  geoserverUrl,
  gisUrl,
  harvestFilterParams,
  harvestWmsParams,
  isGisProxyEnabled,
  wmsEndpointUrl,
} from 'common/geoserver'

const DEM_LAYER_NAME = 'dem'
const DEFAULT_DEM_STYLE = 'gradient'
const DEM_HILLSHADE_STYLE = 'hillshade'

// Static greyscale gradient used as a placeholder legend for the hillshade style,
// which has no ColorMap and cannot produce a meaningful GetLegendGraphic image.
const HILLSHADE_LEGEND_URL = `data:image/svg+xml,${encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" width="20" height="100"><defs><linearGradient id="g" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stop-color="#fff"/><stop offset="100%" stop-color="#444"/></linearGradient></defs><rect width="20" height="100" fill="url(#g)"/></svg>')}`

// createMapParams:
// This function creates the parameters needed for the WMS layer based on the provided layer, legend URL, harvest, and swath parameters.
// It constructs the parameters for the WMS request, including the SLD (Styled Layer Descriptor) and CQL (Common Query Language) filter.
// It also handles custom legends for layers. The if the env parameter is set, it will contain the color and quantity ranges for the legend.
export function createMapParams(
  layer,
  legendUrl,
  swathParameter,
  useCustomLegend,
  lengthUnit,
) {
  const layerParams = {
    tiled: true,
    TRANSPARENT: true,
    SRS: 'EPSG:4326',
    CQL_FILTER: encodeURI(`harvest_id='${layer.harvestId}'`),
    queryable: layer?.queryable || false,
    WIDTH: 600,
    HEIGHT: 600,
    BBOX: layer.bbox,
  }
  let envParam = ''

  if (layer?.customLegends?.length > 0 && !isNil(layer?.customLegends)) {
    let customLegend = layer.customLegends[0]
    let urlSwathParameter = swathParameter
    if (layer.name === 'swath' || layer.layerName === 'swath') {
      customLegend = undefined
      if (swathParameter === 'yield') {
        customLegend = layer.customLegends.find(
          (legend) => legend.parameter === 'yield',
        )
        urlSwathParameter = 'yldg_m2'
      } else if (swathParameter === 'speed') {
        customLegend = layer.customLegends.find(
          (legend) => legend.parameter === 'speed',
        )
        urlSwathParameter = 'speedkmh'
      } else if (swathParameter === 'header_height') {
        customLegend = layer.customLegends.find(
          (legend) => legend.parameter === 'header_height',
        )
        urlSwathParameter = 'header_hi'
      } else {
        customLegend = layer.customLegends.find(
          (legend) => legend.parameter === swathParameter,
        )
      }

      envParam += `p:${urlSwathParameter};`
    }

    if (customLegend && useCustomLegend) {
      const legendData =
        customLegend?.legendData?.legend_data || customLegend?.legendData
      if (layer.name === DEM_LAYER_NAME || layer.layerName === DEM_LAYER_NAME) {
        const ranges = legendData?.num_ranges || 0
        const colors = legendData?.ranges?.c || []
        const quantities = legendData?.ranges?.q || []

        for (let i = 0; i < ranges; i++) {
          if (!isNil(colors[i])) {
            envParam += `c${i}:${colors[i]};`
          }
          if (!isNil(quantities[i])) {
            envParam += `q${i}:${quantities[i]};`
            const label = lengthUnit
              ? parseFloat(
                  unitConvert
                    .convertLength(
                      quantities[i],
                      lengthUnit,
                      unitConvert.METERS,
                    )
                    .toFixed(2),
                )
              : quantities[i]
            envParam += `l${i}:  ${label};` // spaces are intentional to create padding in the legend labels
          }
        }

        layerParams.env = envParam
        layerParams.LAYERS = layer?.name || layer?.layerName
        layerParams.STYLES = legendData?.styleType || DEFAULT_DEM_STYLE
        return layerParams
      }

      const ranges = legendData.num_ranges
      const c = legendData.ranges.c
      const q = legendData.ranges.q
      for (let i = 0; i < ranges; i++) {
        let quantity = q[i]
        envParam += `c${i}:${c[i]};q${i}:${quantity};`
      }
      layerParams.env = encodeURI(envParam)
      layerParams.LAYERS = layer?.name || layer?.layerName
      layerParams.STYLES =
        layer.name === 'swath' || layer.layerName === 'swath'
          ? `${ranges}_ranges_swath`
          : `${ranges}_ranges`
    } else {
      layerParams.SLD = legendUrl
    }
  } else {
    layerParams.SLD = legendUrl
  }
  return layerParams
}

function getWMSFeatureInfoUrl(map, source, coordinates, harvestId, layerName) {
  const viewResolution = map.getView().getResolution()
  // tolerance is somewhat arbitrarily set at 3, further testing can improve it
  const tolerance = 3
  const minX = coordinates[0] - tolerance * 0.5 * viewResolution
  const maxX = coordinates[0] + tolerance * 0.5 * viewResolution
  const minY = coordinates[1] - tolerance * 0.5 * viewResolution
  const maxY = coordinates[1] + tolerance * 0.5 * viewResolution
  return source.getFeatureInfoUrl(coordinates, viewResolution, 'EPSG:3857', {
    INFO_FORMAT: 'application/json',
    BBOX: `${minX}, ${minY}, ${maxX}, ${maxY}`,
    ...harvestFilterParams(harvestId),
    // Set explicitly (rather than relying on the source's own params) because
    // SLD-styled sources deliberately omit LAYERS on GetMap to dodge GeoServer's
    // LAYERS/SLD name-reconciliation bug with unqualified legacy SLD documents.
    LAYERS: layerName,
    QUERY_LAYERS: layerName,
  })
}

export default function MapWMSLayer(props) {
  const {
    layersName = '',
    legendUrl = '',
    harvestId = '',
    mapRef = 'map',
    layerZIndex,
    swathParameters,
  } = props

  let wmsLayerProps = {}
  if (props.wmsLayer) {
    wmsLayerProps = props.wmsLayer
  } else if (props.wmsLayers && props.wmsLayers.length > 0) {
    wmsLayerProps = props.wmsLayers.find((l) => l.name === layersName) || {}
  }
  const wmsLayer = wmsLayerProps

  const dispatch = useDispatch()
  const coords = useSelector((state) => get(state, 'map.pointInfoReq'))
  const swathCoords = useSelector((state) => get(state, 'map.swathInfoReq'))
  const extent = useSelector((state) => get(state, 'map.areaInfoReq'))
  const elevationProfileLine = useSelector((state) =>
    get(state, 'map.elevationProfileReq'),
  )
  const opacity = useSelector((state) => get(state, 'map.layerOpacity', 1.0))
  const previewLegend = useSelector((state) => get(state, 'map.previewLegend'))
  const legendTypes = useSelector((state) => state.field.legendTypes)
  const prefs = useSelector((state) => state.account.singleton.prefs)
  const requestYield = requestYieldUnit(prefs?.units?.yieldunit_in)
  const harvest = useSelector((state) => get(state, 'harvest.current'))
  const [legendName, setLegendName] = useState(legendUrl)
  const [params, setParams] = useState(undefined)
  let map = useMap(mapRef)

  const [source, setSource] = useState()
  const [layer, setLayer] = useState()
  const layerRef = useRef(null)
  // Tracks the serialized params of the last source that was created. When
  // wmsLayer changes reference (but not content) due to the legendUrls feedback
  // loop, this prevents recreating a source with identical WMS params, which
  // would otherwise trigger setSource → changed('foo') → tile reload cycles.
  const lastParamsKeyRef = useRef(null)
  const [swathParameter, setSwathParameter] = useState('yield')
  const [useCustomLegend, setUseCustomLegend] = useState(false)

  useEffect(() => {
    const swathColumn = swathParameters.find(
      (swath) => swath.harvestId === wmsLayer?.harvestId,
    )
    if (swathColumn !== undefined) {
      setSwathParameter(swathColumn.swathParameter)
    }
  }, [wmsLayer, swathParameters])

  useEffect(() => {
    let legendTypeValue
    if (wmsLayer?.name === 'swath') {
      legendTypeValue = legendTypes.find(
        (l) =>
          l.harvestId === wmsLayer.harvestId &&
          l.layerName === 'swath' &&
          l.swathParameter === swathParameter,
      )
    } else if (wmsLayer?.name === 'corrected_yield') {
      legendTypeValue = legendTypes.find(
        (l) =>
          l.harvestId === wmsLayer.harvestId &&
          (l.layerName === 'yield' || l.layerName === 'corrected_yield'), //can change to only corrected yield here
      )
    } else {
      legendTypeValue = legendTypes.find(
        (l) =>
          l.layerName === wmsLayer?.name && l.harvestId === wmsLayer?.harvestId,
      )
    }

    if (legendTypeValue !== undefined || previewLegend) {
      setUseCustomLegend(
        previewLegend ? true : legendTypeValue?.useCustomLegend,
      )
    } else if (wmsLayer?.customLegends?.length > 0) {
      setUseCustomLegend(true)
    }
  }, [legendTypes, wmsLayer, swathParameter, previewLegend])

  useEffect(() => {
    try {
      const lpVersion = harvest?.stats?.lp_version
      if (wmsLayer && Object.keys(wmsLayer).length > 0) {
        const legendName = wmsLayer?.legendUrl

        if (
          isMetric(lpVersion) &&
          useCustomLegend &&
          wmsLayer?.name !== DEM_LAYER_NAME
        ) {
          const layerParams = {
            tiled: true,
            TRANSPARENT: true,
            SRS: 'EPSG:4326',
            CQL_FILTER: encodeURI(`harvest_id='${harvestId}'`),
            queryable: wmsLayer?.queryable || false,
            WIDTH: 600,
            HEIGHT: 600,
            BBOX: wmsLayer.bbox,
          }
          const hctLegendName = legendUrl.replace('.sld', '_hct.sld')
          setLegendName(hctLegendName)
          layerParams.SLD = hctLegendName
          setParams(layerParams)
          dispatch(pointInfoUpdate())
        } else {
          setLegendName(legendName)
          const mapParams = createMapParams(
            wmsLayer,
            legendName,
            swathParameter,
            useCustomLegend,
            prefs?.units?.length_in,
          )
          setParams(mapParams)
          dispatch(pointInfoUpdate())
        }
      }

      function isMetric(lpVersion) {
        return (
          isLegacySLD(lpVersion) &&
          !(
            prefs?.units?.yieldunit_in == 'bu_ac' ||
            prefs?.units?.yieldunit_in == 'lbs_acre'
          ) &&
          layersName !== 'trend_moisture'
        )
      }
    } catch (e) {
      console.log('failed to create map parameters: ', e)
    }
  }, [harvest, prefs, wmsLayer, swathParameter, useCustomLegend])

  useEffect(() => {
    if (params !== undefined) {
      // Bail early when params content has not changed. The wmsLayer reference
      // can change (due to filteredWMSLayers rebuilding after showLayerLegend
      // updates legendUrls) even when the actual WMS params are identical,
      // which would otherwise cause an infinite source-recreation feedback loop.
      const paramsKey = JSON.stringify(params)
      if (paramsKey === lastParamsKeyRef.current) return
      lastParamsKeyRef.current = paramsKey

      // The layer's own legend, which proxy LEGEND variants are relative to.
      // wmsLayer.legendUrl already carries the selected swath variant.
      const proxyContext = {
        harvestId,
        legendUrl: wmsLayer?.layerLegendUrl ?? wmsLayer?.legendUrl,
        layerName: layersName,
      }
      const _source = new TileWMS({
        url: wmsEndpointUrl('wms'),
        serverType: 'geoserver',
        params: harvestWmsParams(params, proxyContext),
        transition: 0,
        tileLoadFunction: geoserverLoadFunction,
      })

      const legend = {
        name: layersName,
        url:
          useCustomLegend && wmsLayer?.name === DEM_LAYER_NAME && params?.STYLES
            ? params.STYLES === DEM_HILLSHADE_STYLE
              ? HILLSHADE_LEGEND_URL
              : _source.getLegendUrl(
                  null,
                  harvestWmsParams(
                    {
                      LAYER: layersName,
                      STYLE: `${params.STYLES}_legend`,
                      env: params.env,
                      LEGEND_OPTIONS:
                        'fontAntiAliasing:true;fontName:SansSerif',
                    },
                    proxyContext,
                  ),
                )
            : _source.getLegendUrl(
                null,
                harvestWmsParams(
                  {
                    LAYER: layersName,
                    SLD: legendName,
                    LEGEND_OPTIONS: 'fontAntiAliasing:true;fontName:SansSerif',
                  },
                  proxyContext,
                ),
              ),
        harvestId: harvestId,
      }

      if (!previewLegend) {
        if (mapRef === 'map') {
          dispatch(showLayerLegend(legend))
        } else if (mapRef === 'map_a') {
          dispatch(showLayerLegendA(legend))
        } else {
          dispatch(showLayerLegendB(legend))
        }
      }

      setSource(_source)
    }
  }, [
    params,
    wmsLayer,
    legendName,
    useCustomLegend,
    swathParameter,
    layersName,
    previewLegend,
  ])

  useEffect(() => {
    if (map && source) {
      if (!layerRef.current) {
        // Create the TileLayer only once with the initial source.
        const _layer = new TileLayer({
          source,
          zIndex: layerZIndex,
          opacity: opacity,
          preload: 1,
          updateWhileAnimating: true,
          updateWhileInteracting: true,
        })
        // The TRANSPARENT WMS param makes GeoServer return
        // transparent-background PNG tiles, so the base imagery shows through.
        layerRef.current = _layer
        setLayer(_layer)
      } else {
        // Subsequent source changes: update the source on the existing layer.
        // setSource keeps old tiles visible while new ones load — no full-canvas
        // clear, no dim flash.
        layerRef.current.setSource(source)
      }
    }
  }, [map, source])

  useEffect(() => {
    if (layer) {
      layer.setOpacity(opacity)
    }
  }, [layer, opacity])

  useEffect(() => {
    if (layer) {
      layer.setZIndex(layerZIndex)
    }
  }, [layer, layerZIndex])

  useEffect(() => {
    if (layer) {
      map.addLayer(layer)
    }

    return () => {
      if (layer) {
        map.removeLayer(layer)
      }
    }
  }, [layer])

  // Clean up layerRef only on unmount to avoid resetting it during source updates.
  // If we null the ref during [layer] dependency changes, it defeats the
  // "create once, update source" optimization: the next source change would
  // re-enter the "if (!layerRef.current)" branch and create a duplicate layer.
  useEffect(() => {
    return () => {
      layerRef.current = null
    }
  }, [])

  useEffect(() => {
    if (
      source &&
      map &&
      source.params_?.queryable &&
      coords &&
      coords[0] !== 0
    ) {
      const url = getWMSFeatureInfoUrl(
        map,
        source,
        coords,
        harvestId,
        layersName,
      )
      dispatch(fetchPointInfo(url))
    }
  }, [map, source, coords])

  useEffect(() => {
    if (
      source &&
      map &&
      source.params_?.queryable &&
      swathCoords &&
      swathCoords[0] !== 0 &&
      layersName === 'swath'
    ) {
      const url = getWMSFeatureInfoUrl(
        map,
        source,
        swathCoords,
        harvestId,
        layersName,
      )
      dispatch(fetchSwathInfo(url, layersName, harvestId))
    }
  }, [harvestId, layersName, map, source, swathCoords])

  useEffect(() => {
    if (extent && extent[0] !== 0 && layersName.length !== 0) {
      const url = isGisProxyEnabled()
        ? `${gisUrl(
            `/harvests/${harvestId}/layers/${layersName}/area`,
          )}?${new URLSearchParams({
            bbox: extent.join(','),
            property: requestYield,
          })}`
        : `${geoserverUrl('/wfs')}?service=WFS&version=2.0.0&request=GetFeature&outputFormat=application/json&bbox=${extent.join(
            ',',
          )},EPSG:3857&typeName=${layersName}&count=100&propertyName=${requestYield}`
      dispatch(fetchAreaInfo(url))
    }
  }, [extent, harvestId, layersName, requestYield])

  useEffect(() => {
    if (elevationProfileLine && layersName === 'dem') {
      // Create WKT from OpenLayers Geometry
      // Assuming elevationProfileLine is an ol/geom/LineString
      let coordinates = elevationProfileLine.getCoordinates() || []

      if (isGisProxyEnabled()) {
        // The proxy builds the WPS request itself from the harvest's own DEM.
        const line = coordinates.map((c) =>
          transform(c, 'EPSG:3857', 'EPSG:4326'),
        )
        dispatch(
          fetchElevationProfile(
            gisUrl(`/harvests/${harvestId}/elevation-profile`),
            JSON.stringify({ line }),
            'application/json',
          ),
        )
        return
      }
      // The coords are likely in EPSG:3857, we must transform them to EPSG:4326 for GeoServer
      const wktLineString = `LINESTRING(${coordinates
        .map((c) => {
          const transformedC = transform(c, 'EPSG:3857', 'EPSG:4326')
          return `${transformedC[0]} ${transformedC[1]}`
        })
        .join(', ')})`

      const [minY, minX, maxY, maxX] = wmsLayer.bbox
        ? wmsLayer.bbox.split(',')
        : [0, 0, 0, 0]

      const xmlPayload = `<?xml version="1.0" encoding="UTF-8"?><wps:Execute version="1.0.0" service="WPS" xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance" xmlns="http://www.opengis.net/wps/1.0.0" xmlns:wfs="http://www.opengis.net/wfs" xmlns:wps="http://www.opengis.net/wps/1.0.0" xmlns:ows="http://www.opengis.net/ows/1.1" xmlns:gml="http://www.opengis.net/gml" xmlns:ogc="http://www.opengis.net/ogc" xmlns:xlink="http://www.w3.org/1999/xlink" xsi:schemaLocation="http://www.opengis.net/wps/1.0.0 http://schemas.opengis.net/wps/1.0.0/wpsAll.xsd">
  <ows:Identifier>gs:LongitudinalProfile</ows:Identifier>
  <wps:DataInputs>
    <wps:Input>
      <ows:Identifier>coverage</ows:Identifier>
      <wps:Reference mimeType="image/tiff" xlink:href="http://geoserver/wcs" method="POST">
        <wps:Body>
          <wcs:GetCoverage xmlns:wcs="http://www.opengis.net/wcs" service="WCS" version="1.0.0">
            <wcs:sourceCoverage>farmtrx_data:${layersName}</wcs:sourceCoverage>
            <wcs:domainSubset>
              <wcs:spatialSubset>
                <gml:Envelope srsName="EPSG:4326">
                  <gml:pos>${minX} ${minY}</gml:pos>
                  <gml:pos>${maxX} ${maxY}</gml:pos>
                </gml:Envelope>
                <gml:Grid dimension="2">
                  <gml:limits>
                    <gml:GridEnvelope>
                      <gml:low>0 0</gml:low>
                      <gml:high>511 511</gml:high>
                    </gml:GridEnvelope>
                  </gml:limits>
                  <gml:axisName>x</gml:axisName>
                  <gml:axisName>y</gml:axisName>
                </gml:Grid>
              </wcs:spatialSubset>
            </wcs:domainSubset>
            <wcs:output>
              <wcs:crs>EPSG:4326</wcs:crs>
              <wcs:format>GeoTIFF</wcs:format>
            </wcs:output>
          </wcs:GetCoverage>
        </wps:Body>
      </wps:Reference>
    </wps:Input>
    <wps:Input>
      <ows:Identifier>geometry</ows:Identifier>
      <wps:Data>
        <wps:ComplexData mimeType="application/wkt"><![CDATA[${wktLineString}]]></wps:ComplexData>
      </wps:Data>
    </wps:Input>
    <wps:Input>
      <ows:Identifier>distance</ows:Identifier>
      <wps:Data>
        <wps:LiteralData>10</wps:LiteralData>
      </wps:Data>
    </wps:Input>
  </wps:DataInputs>
  <wps:ResponseForm>
    <wps:RawDataOutput mimeType="application/json">
      <ows:Identifier>result</ows:Identifier>
    </wps:RawDataOutput>
  </wps:ResponseForm>
</wps:Execute>`

      const url = geoserverUrl('/wps')

      dispatch(fetchElevationProfile(url, xmlPayload))
    }
  }, [elevationProfileLine, harvestId, layersName, wmsLayer])

  return null
}
